import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { prepareClientPhoto } from "@/lib/clientPhoto";
import { ClientAvatar } from "@/components/ClientAvatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import {
  ArrowLeft, Camera, ExternalLink, Plus, Trash2, Save, FileText, BookOpen, Target, UserCheck, Pill, Pencil, Check, CalendarDays, ShieldAlert, TrendingUp, Flame, Printer, ListChecks, X, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import DrillHistory from "@/components/DrillHistory";
import PropertyInventoryPanel from "@/components/PropertyInventoryPanel";
import { DobPopover } from "@/components/admin-client-profile/DobPopover";
import { ProxyEntryButton } from "@/components/admin-client-profile/ProxyEntryButton";
import { MedicationsTab } from "@/components/admin-client-profile/MedicationsTab";
import { GoalEditor } from "@/components/admin-client-profile/GoalEditor";

const newRowId = () => crypto.randomUUID();

const CST_FIELDS = [
  { key: "review_prior_year_services", label: "Review of Prior Year Services", placeholder: "What services were provided last year? Progress, regressions, lessons learned…" },
  { key: "strengths", label: "Strengths", placeholder: "What this client does well — interests, skills, talents…" },
  { key: "barriers", label: "Barriers", placeholder: "Communication, mobility, behavioral, or environmental barriers…" },
  { key: "communication_style", label: "Communication Style / Preference", placeholder: "Verbal, sign, AAC device, gestures, written cues…" },
  { key: "relationships", label: "Relationships", placeholder: "Important people in their life — family, friends, peers, staff…" },
  { key: "pets", label: "Pets", placeholder: "Pets in the home and the client's interaction with them…" },
  { key: "hopes_dreams_fears", label: "Hopes / Dreams / Fears", placeholder: "Personal goals, aspirations, and known fears or anxieties…" },
  { key: "health_safety_issues", label: "Health and Safety Issues", placeholder: "Diagnoses, allergies, fall risk, choking risk, seizure protocol…" },
  { key: "hygiene_assistance", label: "Hygiene Assistance", placeholder: "Level and type of assistance needed for personal care…" },
  { key: "transportation", label: "Transportation", placeholder: "How the client gets around — vehicle, wheelchair, paratransit…" },
  { key: "legal_rights_issues", label: "Legal / Rights Issues", placeholder: "Guardianship status, court orders, rights restrictions…" },
  { key: "possible_behaviors", label: "Possible Behaviors", placeholder: "Behaviors staff may encounter and triggers to watch for…" },
  { key: "behavior_interventions", label: "Behavior Interventions", placeholder: "Approved redirection / de-escalation strategies and what NOT to do…" },
  { key: "unique_details", label: "Unique Details", placeholder: "Quirks, preferences, sensitivities, and other context that helps staff support this person well…" },
];

// Compute age from a YYYY-MM-DD DOB string. Returns null if invalid.
const computeAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

export default function AdminClientProfile() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [client, setClient] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [goals, setGoals] = useState([]);
  const [newGoal, setNewGoal] = useState({ title: "", description: "" });
  const [medsCount, setMedsCount] = useState(0);
  const [editingTrainingId, setEditingTrainingId] = useState(null);

  useEffect(() => {
    api.get(`/clients/${clientId}`).then(({ data }) => {
      // Normalize doc_links: ensure list shape with ids
      const links = Array.isArray(data.doc_links) ? data.doc_links : [];
      const normalized = { ...data, doc_links: links.map((d) => ({ ...d, id: d.id || newRowId() })) };
      setClient(normalized); setDraft(normalized);
    });
    api.get("/contractors").then(({ data }) => setContractors(data));
    api.get(`/clients/${clientId}/goals`).then(({ data }) => setGoals(data));
    api.get(`/medications?client_id=${clientId}`).then(({ data }) => setMedsCount(data.length)).catch(() => {});
  }, [clientId]);

  if (!draft) return <p className="text-muted-foreground">Loading…</p>;

  const update = (patch) => setDraft({ ...draft, ...patch });
  // Singleton contact updaters (guardian, sc, behaviorist)
  const updateContact = (k, patch) => setDraft({ ...draft, contacts: { ...draft.contacts, [k]: { ...(draft.contacts?.[k] || {}), ...patch } } });

  // Family contacts list (repeatable multi-entry)
  const familyRows = Array.isArray(draft.contacts?.family) ? draft.contacts.family : [];
  const setFamily = (rows) => setDraft({ ...draft, contacts: { ...draft.contacts, family: rows } });
  const addFamily = () => setFamily([...familyRows, { id: newRowId(), name: "", phone: "", relation: "" }]);
  const updateFamily = (id, patch) => setFamily(familyRows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeFamily = (id) => setFamily(familyRows.filter((r) => r.id !== id));

  // Custom CST sections
  const customSections = draft.custom_sections || [];
  const addCustomSection = () => update({ custom_sections: [...customSections, { id: newRowId(), label: "New Panel", content: "" }] });
  const updateCustomSection = (id, patch) => update({ custom_sections: customSections.map((s) => s.id === id ? { ...s, ...patch } : s) });
  const removeCustomSection = (id) => update({ custom_sections: customSections.filter((s) => s.id !== id) });

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // v40 — Persist the photo IMMEDIATELY to the dedicated upload endpoint
    // (was: staging in draft.photo_data_url and hoping the user hits Save).
    // Auto-downscales large phone photos through a canvas re-encode so the
    // upload always fits under the 10MB backend cap.
    try {
      const dataUrl = await prepareClientPhoto(file);
      const { data } = await api.post(`/clients/${clientId}/photo`, { photo_data_url: dataUrl });
      // Reflect the persisted URL (not a base64 blob) in the local draft
      // so the avatar refreshes and subsequent PUT /clients/{cid} saves
      // don't overwrite the just-saved URL with a stale base64 payload.
      update({ photo_data_url: data.photo_data_url });
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err?.message || formatApiError(err.response?.data?.detail) || "Photo upload failed");
    } finally {
      // Reset the input so re-selecting the same file re-triggers onChange.
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: draft.name,
        dob: draft.dob || "",
        gender: draft.gender || "",
        contractor_id: draft.contractor_id,
        photo_data_url: draft.photo_data_url,
        personal_id: draft.personal_id || "",
        behavior_triggers: (draft.behavior_triggers || []).filter((t) => (t.label || "").trim()),
        interactive_widgets: (draft.interactive_widgets || []).filter((w) => (w.label || "").trim() && w.type),
        contacts: {
          family: familyRows.filter((r) => (r.name || "").trim() || (r.phone || "").trim()),
          guardian: draft.contacts?.guardian || {},
          sc: draft.contacts?.sc || {},
          behaviorist: draft.contacts?.behaviorist || {},
        },
        doc_links: (draft.doc_links || []).filter((d) => (d.label || "").trim()),
        training_blocks: draft.training_blocks || [],
        emar_disabled: !!draft.emar_disabled,
        // v28 — Structured Document Link Hub URLs
        pcsp_url: draft.pcsp_url || "",
        support_strategy_url: draft.support_strategy_url || "",
        room_board_url: draft.room_board_url || "",
        bsp_url: draft.bsp_url || "",
        // v32 — Budget Settings (admin-only HHS financial tracking)
        plan_start_date: draft.plan_start_date || "",
        plan_end_date: draft.plan_end_date || "",
        total_authorized_budget: draft.total_authorized_budget ?? null,
        total_hhs_days_authorized: draft.total_hhs_days_authorized ?? null,
        hhs_days_billed_to_date: draft.hhs_days_billed_to_date ?? null,
        custom_sections: customSections.filter((s) => (s.label || "").trim()),
        ...Object.fromEntries(CST_FIELDS.map((f) => [f.key, draft[f.key] || ""])),
      };
      const { data } = await api.put(`/clients/${clientId}`, payload);
      setClient(data); setDraft({ ...data, doc_links: (data.doc_links || []).map((d) => ({ ...d, id: d.id || newRowId() })) });
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  // Training (now inside Client Details)
  const addTraining = () => {
    const newBlock = { id: newRowId(), title: "New training topic", content: "" };
    update({ training_blocks: [...(draft.training_blocks || []), newBlock] });
    setEditingTrainingId(newBlock.id);  // open in edit mode immediately
  };
  const updateTraining = (id, patch) => {
    update({ training_blocks: (draft.training_blocks || []).map((b) => (b.id === id ? { ...b, ...patch } : b)) });
  };
  const removeTraining = (id) => {
    update({ training_blocks: (draft.training_blocks || []).filter((b) => b.id !== id) });
  };

  // Per-client dynamic document links
  const addDocLink = () => {
    update({ doc_links: [...(draft.doc_links || []), { id: newRowId(), label: "", url: "" }] });
  };
  const updateDocLink = (id, patch) => {
    update({ doc_links: (draft.doc_links || []).map((d) => (d.id === id ? { ...d, ...patch } : d)) });
  };
  const removeDocLink = (id) => {
    update({ doc_links: (draft.doc_links || []).filter((d) => d.id !== id) });
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-client-profile">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/profiles")} data-testid="back-to-profiles">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <ProxyEntryButton clientId={clientId} contractorId={draft.contractor_id} />
      </div>

      <Card className="p-6 flex items-start gap-5 flex-wrap" data-testid="client-profile-header">
        <div className="relative">
          <ClientAvatar
            name={draft.name}
            photoUrl={draft.photo_data_url}
            className="h-28 w-28 rounded-full object-cover border-2 border-primary/20 shadow-sm"
            placeholderClassName="h-28 w-28 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display font-bold text-3xl border-2 border-primary/20"
            testId="client-avatar"
          />
          <div className="grid grid-cols-2 gap-1.5 mt-2">
            <Button
              size="sm" variant="outline" className="text-[11px] h-8"
              onClick={() => fileRef.current?.click()}
              data-testid="upload-photo"
            ><Camera className="w-3 h-3 mr-1" /> Photo</Button>
            <Button
              size="sm" variant="outline" className="text-[11px] h-8"
              onClick={() => cameraRef.current?.click()}
              data-testid="capture-photo"
            ><Camera className="w-3 h-3 mr-1" /> Camera</Button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="admin-photo-input" />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="hidden" data-testid="admin-camera-input" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-label">Client Profile</p>
          <Input
            value={draft.name || ""} onChange={(e) => update({ name: e.target.value })}
            className="font-display font-bold text-2xl border-0 px-0 h-auto focus-visible:ring-0 shadow-none"
            data-testid="client-name-input"
          />
          {(() => {
            const age = computeAge(draft.dob);
            return (
              <p className="text-sm text-muted-foreground mt-0.5" data-testid="client-age-display">
                {age != null ? `${age} years old` : "Age — set Date of Birth below"}
              </p>
            );
          })()}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div>
              <Label className="text-label">Date of Birth</Label>
              <DobPopover
                value={draft.dob || ""}
                onChange={(v) => update({ dob: v })}
                testId="client-dob"
              />
            </div>
            <div>
              <Label className="text-label">Gender</Label>
              <Select value={draft.gender || ""} onValueChange={(v) => update({ gender: v })}>
                <SelectTrigger className="mt-1" data-testid="client-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-label">Personal ID (PID)</Label>
              <Input
                value={draft.personal_id || ""}
                onChange={(e) => update({ personal_id: e.target.value.replace(/\D/g, "") })}
                placeholder="DSPD PID"
                inputMode="numeric"
                className="mt-1 font-mono"
                data-testid="client-personal-id"
              />
            </div>
            <div>
              <Label className="text-label">Contractor</Label>
              <Select value={draft.contractor_id || ""} onValueChange={(v) => update({ contractor_id: v })}>
                <SelectTrigger className="mt-1" data-testid="client-contractor-select"><SelectValue /></SelectTrigger>
                <SelectContent>{contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-client-profile">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save"}
        </Button>
      </Card>

      {/* v24 — Admin-only quick controls: layout cascade override + archive */}
      <Card className="p-4 flex items-center justify-between flex-wrap gap-3" data-testid="client-admin-controls">
        <div className="flex items-center gap-2 flex-wrap" data-testid="client-layout-toggle">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Daily Note layout:</span>
          {[
            { v: null, label: "Inherit from contractor" },
            { v: "compact_checkbox", label: "Compact" },
            { v: "detailed_narrative", label: "Detailed narrative" },
          ].map((opt) => {
            const active = (draft.ui_layout || null) === opt.v;
            return (
              <Button
                key={opt.label}
                size="sm"
                variant={active ? "default" : "outline"}
                className="h-7 text-[11px] px-2"
                onClick={async () => {
                  try {
                    await api.put(`/clients/${clientId}`, { ui_layout: opt.v });
                    setDraft((d) => ({ ...d, ui_layout: opt.v }));
                    toast.success("Layout updated");
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}
                data-testid={`client-layout-${opt.v || "inherit"}`}
              >
                {opt.label}
              </Button>
            );
          })}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            const action = draft.is_archived ? "unarchive" : "archive";
            if (!window.confirm(`${action === "archive" ? "Archive" : "Restore"} ${draft.name}?`)) return;
            try {
              await api.post(`/clients/${clientId}/${action}`);
              setDraft((d) => ({ ...d, is_archived: !d.is_archived }));
              toast.success(action === "archive" ? "Client archived" : "Client restored");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
          }}
          data-testid="archive-client-btn"
        >
          {draft.is_archived ? "Restore client" : "Archive client"}
        </Button>
      </Card>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "details"}>
        <TabsList>
          <TabsTrigger value="details" data-testid="tab-details">Client Details</TabsTrigger>
          <TabsTrigger value="goals" data-testid="tab-goals">Client Goals</TabsTrigger>
          <TabsTrigger value="medications" data-testid="tab-medications">Medication List</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Points of Contact</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Document Hub</TabsTrigger>
          <TabsTrigger value="property" data-testid="tab-property">📋 Property Inventory</TabsTrigger>
          <TabsTrigger value="budget" data-testid="tab-budget">Budget Settings</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <div className="grid md:grid-cols-[1.2fr_1fr] gap-4 items-start">
            <Card className="p-5 space-y-3" data-testid="training-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /><p className="text-label">Client-Specific Training</p></div>
                <Button onClick={addTraining} size="sm" data-testid="add-training-block"><Plus className="w-4 h-4 mr-1" /> Add Topic</Button>
              </div>
              {(draft.training_blocks || []).length === 0 && (
                <p className="text-sm text-muted-foreground py-4 text-center">No training topics yet. Add one to document client-specific protocols.</p>
              )}
              {(draft.training_blocks || []).map((b) => {
                const isEditing = !!editingTrainingId && editingTrainingId === b.id;
                return (
                  <div key={b.id} className="border border-border rounded-md p-3 space-y-2 bg-secondary/30" data-testid={`training-block-${b.id}`}>
                    {isEditing ? (
                      <>
                        <div className="flex items-center gap-2">
                          <Input
                            value={b.title}
                            onChange={(e) => updateTraining(b.id, { title: e.target.value })}
                            className="font-display font-semibold"
                            data-testid={`training-title-${b.id}`}
                          />
                          <Button variant="ghost" size="sm" onClick={() => setEditingTrainingId(null)} data-testid={`done-training-${b.id}`}>
                            <Check className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removeTraining(b.id)} data-testid={`remove-training-${b.id}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                        <Textarea
                          rows={4}
                          value={b.content || ""}
                          onChange={(e) => updateTraining(b.id, { content: e.target.value })}
                          placeholder="Describe the protocol, training, or instruction..."
                          data-testid={`training-content-${b.id}`}
                        />
                      </>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-display font-semibold text-sm" data-testid={`training-title-view-${b.id}`}>{b.title || "(untitled topic)"}</p>
                          {b.content && (
                            <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-1" data-testid={`training-content-view-${b.id}`}>{b.content}</p>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => setEditingTrainingId(b.id)} data-testid={`edit-training-${b.id}`}>
                            <Pencil className="w-4 h-4 text-primary" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removeTraining(b.id)} data-testid={`remove-training-${b.id}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
            {/* Right column: CST consolidated free-text fields */}
            <div className="space-y-3" data-testid="cst-panel">
              {CST_FIELDS.map((f) => (
                <Card key={f.key} className="p-4">
                  <Label className="text-label">{f.label}</Label>
                  <Textarea
                    rows={3}
                    value={draft[f.key] || ""}
                    onChange={(e) => update({ [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="mt-1"
                    data-testid={`cst-${f.key}`}
                  />
                </Card>
              ))}

              {/* Dynamic custom sections */}
              <Card className="p-4 space-y-3" data-testid="custom-sections-panel">
                <div className="flex items-center justify-between">
                  <p className="text-label">Custom Tracking Panels</p>
                  <Button size="sm" onClick={addCustomSection} data-testid="add-custom-section">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Custom Section
                  </Button>
                </div>
                {(draft.custom_sections || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Need to track something not in the standard CST fields? Add a custom panel with your own label.
                  </p>
                )}
                {(draft.custom_sections || []).map((s) => (
                  <div key={s.id} className="border border-border rounded-md p-3 bg-secondary/30 space-y-2" data-testid={`custom-section-${s.id}`}>
                    <div className="flex items-center gap-2">
                      <Input
                        value={s.label || ""}
                        onChange={(e) => updateCustomSection(s.id, { label: e.target.value })}
                        placeholder="Section label"
                        className="font-display font-semibold"
                        data-testid={`custom-section-label-${s.id}`}
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeCustomSection(s.id)} data-testid={`remove-custom-section-${s.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                    <Textarea
                      rows={3}
                      value={s.content || ""}
                      onChange={(e) => updateCustomSection(s.id, { content: e.target.value })}
                      placeholder="Tracking notes…"
                      data-testid={`custom-section-content-${s.id}`}
                    />
                  </div>
                ))}
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="medications">
          <MedicationsTab clientId={clientId} />
        </TabsContent>

        <TabsContent value="goals">
          <Card className="p-5 space-y-3" data-testid="goals-section">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-primary" />
              <div>
                <p className="text-label">Active Client Goals — Form Configuration Matrix</p>
                <p className="text-[11px] text-muted-foreground">
                  PCSP support sub-checkboxes render under each goal on the contractor&apos;s Daily Note. Check / uncheck them here to dynamically control what the provider sees.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {goals.length === 0 && <p className="text-sm text-muted-foreground">No active goals.</p>}
              {goals.map((g) => (
                <GoalEditor
                  key={g.id}
                  clientId={clientId}
                  goal={g}
                  onSaved={(updated) => setGoals((gs) => gs.map((x) => x.id === updated.id ? updated : x))}
                  onDeleted={() => setGoals((gs) => gs.filter((x) => x.id !== g.id))}
                />
              ))}
            </div>
            <div className="border-t border-border pt-3 space-y-2">
              <Label className="text-label">Goal</Label>
              <Input value={newGoal.title} onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })} placeholder="Goal" data-testid="new-goal-title" />
              <Label className="text-label">Support Details</Label>
              <Textarea value={newGoal.description} onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })} placeholder="Support details (strategies, prompts, schedules…)" rows={3} data-testid="new-goal-desc" />
              <Button onClick={async () => {
                if (!newGoal.title.trim()) return toast.error("Goal is required");
                const { data } = await api.post(`/clients/${clientId}/goals`, newGoal);
                setGoals((gs) => [...gs, data]);
                setNewGoal({ title: "", description: "" });
                toast.success("Goal added");
              }} data-testid="add-goal"><Plus className="w-4 h-4 mr-1" /> Add Client Goal</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="contacts">
          <Card className="p-5 space-y-3" data-testid="contact-family">
            <div className="flex items-center justify-between">
              <p className="text-label">Family / Friends</p>
              <Button size="sm" onClick={addFamily} data-testid="add-family-contact"><Plus className="w-4 h-4 mr-1" /> Add Contact</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Add as many family members and friends as needed. Each row captures a separate Name, Phone, and Relationship.
            </p>
            {familyRows.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-3">No family/friend contacts yet. Click "Add Contact" to start.</p>
            )}
            {familyRows.map((row, idx) => (
              <div key={row.id} className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1.2fr_auto] gap-2 items-center" data-testid={`family-row-${idx}`}>
                <Input placeholder="Name" value={row.name || ""} onChange={(e) => updateFamily(row.id, { name: e.target.value })} data-testid={`family-name-${idx}`} />
                <Input placeholder="Phone" value={row.phone || ""} onChange={(e) => updateFamily(row.id, { phone: e.target.value })} data-testid={`family-phone-${idx}`} />
                <Input placeholder="Relationship Role (e.g., Mother, Brother, Friend)" value={row.relation || ""} onChange={(e) => updateFamily(row.id, { relation: e.target.value })} data-testid={`family-relation-${idx}`} />
                <Button variant="ghost" size="sm" onClick={() => removeFamily(row.id)} data-testid={`remove-family-${idx}`}>
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            ))}
          </Card>

          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {[
              { id: "guardian", label: "Guardian" },
              { id: "sc", label: "Support Coordinator (SC)" },
              { id: "behaviorist", label: "Behaviorist" },
            ].map((ck) => {
              const v = draft.contacts?.[ck.id] || {};
              return (
                <Card key={ck.id} className="p-5 space-y-2" data-testid={`contact-${ck.id}`}>
                  <p className="text-label">{ck.label}</p>
                  <Input placeholder="Name" value={v.name || ""} onChange={(e) => updateContact(ck.id, { name: e.target.value })} />
                  <Input placeholder="Relation / Title" value={v.relation || ""} onChange={(e) => updateContact(ck.id, { relation: e.target.value })} />
                  <Input placeholder="Phone" value={v.phone || ""} onChange={(e) => updateContact(ck.id, { phone: e.target.value })} />
                  <Input placeholder="Email" value={v.email || ""} onChange={(e) => updateContact(ck.id, { email: e.target.value })} />
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="docs">
          <div className="space-y-4">
          {/* v28 — Structured Document Link Hub: 5 fixed slots in mandated order */}
          <Card className="p-5 space-y-3" data-testid="structured-doc-hub">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <div>
                <p className="text-label">Document Link Hub</p>
                <p className="text-[11px] text-muted-foreground">
                  Paste view-only Google Drive (or any) URLs. These render as clickable buttons on the contractor&apos;s Client Profile in the order shown below.
                </p>
              </div>
            </div>
            {[
              { key: "pcsp_url", label: "1. Person-Centered Service Plan (PCSP)", optional: false },
              { key: "support_strategy_url", label: "2. Support Strategy", optional: false },
              { key: "room_board_url", label: "3. Room &amp; Board Agreement", optional: false },
              { key: "bsp_url", label: "4. Behavior Support Plan (BSP)", optional: true },
            ].map(({ key, label, optional }) => (
              <div key={key} className="space-y-1.5" data-testid={`structured-link-${key}`}>
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <span dangerouslySetInnerHTML={{ __html: label }} />
                  {optional && <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-semibold">Optional</span>}
                </Label>
                <div className="grid grid-cols-[1fr_auto] gap-2">
                  <Input
                    type="url"
                    placeholder="https://drive.google.com/..."
                    value={draft[key] || ""}
                    onChange={(e) => update({ [key]: e.target.value })}
                    data-testid={`structured-link-input-${key}`}
                  />
                  <Button variant="outline" size="sm" disabled={!draft[key]} asChild>
                    <a href={draft[key] || "#"} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </Card>

          <Card className="p-5 space-y-3" data-testid="doc-hub-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                <div>
                  <p className="text-label">5. Other Resources</p>
                  <p className="text-[11px] text-muted-foreground">
                    Add freeform resource links with custom labels (Rights Restrictions, ISP, agency notes, etc.).
                  </p>
                </div>
              </div>
              <Button onClick={addDocLink} size="sm" data-testid="add-doc-link"><Plus className="w-4 h-4 mr-1" /> Add link</Button>
            </div>
            {(draft.doc_links || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No other resources yet. Add one above.</p>
            )}
            {(draft.doc_links || []).map((d) => (
              <div key={d.id} className="grid md:grid-cols-[260px_1fr_auto_auto] gap-2 items-center" data-testid={`doc-link-${d.id}`}>
                <Input
                  placeholder="Label (e.g., Rights Restrictions)"
                  value={d.label || ""}
                  onChange={(e) => updateDocLink(d.id, { label: e.target.value })}
                  data-testid={`doc-label-${d.id}`}
                />
                <Input
                  placeholder="https://drive.google.com/..."
                  value={d.url || ""}
                  onChange={(e) => updateDocLink(d.id, { url: e.target.value })}
                  data-testid={`doc-url-${d.id}`}
                />
                <Button variant="outline" size="sm" disabled={!d.url} asChild>
                  <a href={d.url || "#"} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => removeDocLink(d.id)}>
                  <Trash2 className="w-4 h-4 text-red-600" />
                </Button>
              </div>
            ))}
          </Card>

          {/* v19 — Quarterly Evacuation Drill history with DSPD print buttons */}
          <DrillHistory clientId={clientId} compact />
          </div>
        </TabsContent>

        <TabsContent value="property">
          <PropertyInventoryPanel clientId={clientId} />
        </TabsContent>

        <TabsContent value="budget">
          <Card className="p-5 space-y-4" data-testid="budget-settings-card">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" />
              <div>
                <p className="text-label">Budget Settings (Admin Only)</p>
                <p className="text-[11px] text-muted-foreground">
                  HHS plan cycle financials for this client. Drives the Client Budget Tracking dashboard.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-label">Plan start date</Label>
                <Input
                  type="date"
                  value={draft.plan_start_date || ""}
                  onChange={(e) => update({ plan_start_date: e.target.value })}
                  data-testid="budget-plan-start"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-label">Plan end date</Label>
                <Input
                  type="date"
                  value={draft.plan_end_date || ""}
                  onChange={(e) => update({ plan_end_date: e.target.value })}
                  data-testid="budget-plan-end"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-label">Total authorized budget ($)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  placeholder="e.g. 160037.51"
                  value={draft.total_authorized_budget ?? ""}
                  onChange={(e) => update({ total_authorized_budget: e.target.value === "" ? null : parseFloat(e.target.value) })}
                  data-testid="budget-total-authorized"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-label">Total HHS days authorized</Label>
                <Input
                  type="number" step="1" min="0"
                  placeholder="e.g. 357"
                  value={draft.total_hhs_days_authorized ?? ""}
                  onChange={(e) => update({ total_hhs_days_authorized: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                  data-testid="budget-days-authorized"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label className="text-label">HHS days billed to date</Label>
                <Input
                  type="number" step="1" min="0"
                  placeholder="e.g. 50"
                  value={draft.hhs_days_billed_to_date ?? ""}
                  onChange={(e) => update({ hhs_days_billed_to_date: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                  data-testid="budget-days-billed"
                />
                <p className="text-[11px] text-muted-foreground">
                  Daily burn rate is derived automatically from authorized budget ÷ authorized days. Burn-rate alert turns amber at 80% utilization, red at 95% or when running &gt;15pts ahead of the elapsed timeline.
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <div className="space-y-4">
            <Card className="p-5 space-y-4" data-testid="client-settings">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Pill className="w-4 h-4 text-primary" />
                    <p className="font-display font-semibold">Disable eMAR for this client</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 max-w-md">
                    When enabled, providers won't see the eMAR / medication section on Daily Notes.
                    Use this when a client has no assigned medications so contractors aren't forced to
                    interact with empty medication logs. Currently: {medsCount} active medication{medsCount === 1 ? "" : "s"}.
                  </p>
                </div>
                <Switch
                  checked={!!draft.emar_disabled}
                  onCheckedChange={(v) => update({ emar_disabled: v })}
                  data-testid="emar-disabled-toggle"
                />
              </div>
            </Card>

            {/* v18 — Behavior Triggers editor (per-client high-risk escalation rules) */}
            <Card className="p-5 space-y-3" data-testid="behavior-triggers-editor">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-red-600" />
                  <p className="font-display font-semibold">Behavior &amp; Risk Triggers</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => update({
                    behavior_triggers: [...(draft.behavior_triggers || []), { id: newRowId(), label: "" }],
                  })}
                  data-testid="add-trigger"
                ><Plus className="w-4 h-4 mr-1" /> Add trigger</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Each trigger shows up as a checkbox on this client's Daily Notes form. Toggling it ON
                forces the contractor to capture an Antecedent / Behavior / De-escalation /
                Notification escalation entry.
              </p>
              {(draft.behavior_triggers || []).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-3">No behavior triggers configured for this client.</p>
              )}
              <div className="space-y-2">
                {(draft.behavior_triggers || []).map((t, idx) => (
                  <div key={t.id || `t-${idx}`} className="grid grid-cols-[1fr_auto] gap-2" data-testid={`trigger-row-${idx}`}>
                    <Input
                      placeholder="e.g., Property destruction"
                      value={t.label || ""}
                      onChange={(e) => update({
                        behavior_triggers: (draft.behavior_triggers || []).map((x, i) => i === idx ? { ...x, label: e.target.value } : x),
                      })}
                      data-testid={`trigger-label-${idx}`}
                    />
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => update({
                        behavior_triggers: (draft.behavior_triggers || []).filter((_, i) => i !== idx),
                      })}
                      data-testid={`trigger-remove-${idx}`}
                    ><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                ))}
              </div>
            </Card>

            {/* v18 — Interactive Widgets editor (counters / verification checkboxes) */}
            <Card className="p-5 space-y-3" data-testid="widgets-editor">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-sky-600" />
                  <p className="font-display font-semibold">Interactive Daily Tracking Widgets</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => update({
                    interactive_widgets: [...(draft.interactive_widgets || []), { id: newRowId(), type: "counter", label: "", target: "" }],
                  })}
                  data-testid="add-widget"
                ><Plus className="w-4 h-4 mr-1" /> Add widget</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Counter = large +/- buttons on Daily Notes (e.g., Hydration cups). Checkbox = mandatory
                attestation row (e.g., 2-Hour Hygiene Schedule Review).
              </p>
              {(draft.interactive_widgets || []).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-3">No interactive widgets configured for this client.</p>
              )}
              <div className="space-y-3">
                {(draft.interactive_widgets || []).map((w, idx) => (
                  <div key={w.id || `w-${idx}`} className="grid md:grid-cols-[140px_1.4fr_1fr_auto] gap-2 items-center" data-testid={`widget-row-${idx}`}>
                    <Select
                      value={w.type || "counter"}
                      onValueChange={(v) => update({
                        interactive_widgets: (draft.interactive_widgets || []).map((x, i) => i === idx ? { ...x, type: v } : x),
                      })}
                    >
                      <SelectTrigger data-testid={`widget-type-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="counter">Counter (+/-)</SelectItem>
                        <SelectItem value="checkbox">Checkbox (attestation)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Label (e.g., Hydration Volume Counter)"
                      value={w.label || ""}
                      onChange={(e) => update({
                        interactive_widgets: (draft.interactive_widgets || []).map((x, i) => i === idx ? { ...x, label: e.target.value } : x),
                      })}
                      data-testid={`widget-label-${idx}`}
                    />
                    <Input
                      placeholder="Target / hint (optional)"
                      value={w.target || ""}
                      onChange={(e) => update({
                        interactive_widgets: (draft.interactive_widgets || []).map((x, i) => i === idx ? { ...x, target: e.target.value } : x),
                      })}
                      data-testid={`widget-target-${idx}`}
                    />
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => update({
                        interactive_widgets: (draft.interactive_widgets || []).filter((_, i) => i !== idx),
                      })}
                      data-testid={`widget-remove-${idx}`}
                    ><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

